import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Chip
} from '@mui/material';
import { Upload, Trash2, Plus, Send, Edit, MoreVertical } from 'lucide-react';
import { format } from 'date-fns';
import BankStatementViewer from './BankStatementViewer';
import MissingReceiptLedger from './admin/MissingReceiptLedger';
import ReceiptUploader from './ReceiptUploader';
import WebPortalAllocationPreview from './WebPortalAllocationPreview';
import { descriptionLooksLikeWebPortal } from '../utils/webPortal';
import GLCodeField from './admin/GLCodeField';

// Standardized error messages matching the backend
const ERROR_MESSAGES = {
  FILE_TYPE: 'Only JPG, PNG, and PDF files are allowed',
  FILE_SIZE: 'File size cannot exceed 10MB',
  MAX_FILES: 'Maximum of 50 receipts allowed',
  TOTAL_FILES: 'Cannot exceed 50 total receipts'
};

// TabPanel component for the tabbed interface
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`expense-tab-${index}`}
      aria-labelledby={`expense-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

const ExpenseReportForm = ({ report: initialReport, onSubmit }) => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [report, setReport] = useState(initialReport);
  const [items, setItems] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [mrlItems, setMrlItems] = useState([]);
  const [mrlSignature, setMrlSignature] = useState('');
  const [bankStatement, setBankStatement] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
  const [newItemDialogOpen, setNewItemDialogOpen] = useState(false);
  const [editItemDialogOpen, setEditItemDialogOpen] = useState(false);
  const [confirmDeleteItemOpen, setConfirmDeleteItemOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuItemId, setActionMenuItemId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [newItem, setNewItem] = useState({
    work_order: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    description: '',
    gl_account: '',
    amount: '',
    is_web_portal: false,
    webPortalManual: false
  });
  const [editItem, setEditItem] = useState({
    id: null,
    work_order: '',
    date: '',
    description: '',
    gl_account: '',
    amount: '',
    is_web_portal: false,
    webPortalManual: true
  });
  const [canTagWebPortal, setCanTagWebPortal] = useState(false);
  const [webPortalAllocation, setWebPortalAllocation] = useState(null);

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const today = new Date();
      const response = await apiService.getOrCreateMonthlyReport({
        month: today.getMonth() + 1,
        year: today.getFullYear(),
        create_new: true
      });

      // Ensure total_amount is a number
      if (response.report && response.report.total_amount) {
        response.report.total_amount = typeof response.report.total_amount === 'number' 
          ? response.report.total_amount 
          : parseFloat(response.report.total_amount) || 0;
      }

      setReport(response.report);
      setItems(response.items || []);
      setReceipts(response.receipts || []);
      setMrlItems(response.mrlItems || []);
      setMrlSignature(response.report.mrl_signature);
      setBankStatement(response.bankStatement);
      setCanTagWebPortal(Boolean(response.can_tag_web_portal));
      setWebPortalAllocation(response.web_portal_allocation || null);
      
      setLoading(false);
    } catch (err) {
      setError('Failed to load expense report: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
    try {
      setError(null);
      const addedItem = await apiService.addExpenseItem(report.id, {
        work_order: newItem.work_order,
        date: newItem.date,
        description: newItem.description,
        gl_account: newItem.gl_account,
        amount: newItem.amount,
        is_web_portal: newItem.is_web_portal
      });
      setItems([...items, addedItem]);
      setNewItemDialogOpen(false);
      setNewItem({
        work_order: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        description: '',
        gl_account: '',
        amount: '',
        is_web_portal: false,
        webPortalManual: false
      });
      setSuccess('Expense item added successfully');
      
      // Refresh the report to get updated total
      await loadReport();
    } catch (err) {
      setError('Failed to add expense item: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEditItem = async () => {
    try {
      setError(null);
      
      const updatedItem = await apiService.updateExpenseItem(report.id, editItem.id, {
        work_order: editItem.work_order,
        date: editItem.date,
        description: editItem.description,
        gl_account: editItem.gl_account,
        amount: editItem.amount,
        is_web_portal: editItem.is_web_portal
      });
      
      setItems(items.map((item) => (item.id === updatedItem.id ? updatedItem : item)));
      
      setEditItemDialogOpen(false);
      setSuccess('Expense item updated successfully');
      
      // Refresh the report to get updated total
      await loadReport();
    } catch (err) {
      setError('Failed to update expense item: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteItem = async () => {
    if (!selectedItem) return;
    
    try {
      setError(null);
      await apiService.deleteExpenseItem(report.id, selectedItem.id);
      setItems(items.filter(item => item.id !== selectedItem.id));
      setConfirmDeleteItemOpen(false);
      setSelectedItem(null);
      setSuccess('Expense item deleted successfully');
      
      // Refresh the report to get updated total
      await loadReport();
    } catch (err) {
      setError('Failed to delete expense item: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleOpenActionMenu = (event, item) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuItemId(item.id);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuItemId(null);
  };

  const handleOpenEditDialog = (item) => {
    // Handle date without timezone conversion
    let dateValue = '';
    if (item.date) {
      // If date is already in YYYY-MM-DD format, use it directly
      if (typeof item.date === 'string' && item.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        dateValue = item.date;
      } else {
        // Parse date carefully to avoid timezone issues
        const dateStr = item.date.toString();
        if (dateStr.includes('T')) {
          // If it's an ISO string, take just the date part
          dateValue = dateStr.split('T')[0];
        } else {
          dateValue = dateStr;
        }
      }
    }
    
    setEditItem({
      id: item.id,
      work_order: item.work_order || '',
      date: dateValue,
      description: item.description || '',
      gl_account: item.gl_account || '',
      amount: item.amount !== undefined ? item.amount.toString() : '',
      is_web_portal: Boolean(item.is_web_portal),
      webPortalManual: true
    });
    setEditItemDialogOpen(true);
    handleCloseActionMenu();
  };

  const handleOpenDeleteDialog = (item) => {
    setSelectedItem(item);
    setConfirmDeleteItemOpen(true);
    handleCloseActionMenu();
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files.length) return;

    // Validate number of files
    if (files.length > 50) {
      setError(ERROR_MESSAGES.MAX_FILES);
      return;
    }

    // Validate total number of receipts
    if (receipts.length + files.length > 50) {
      setError(ERROR_MESSAGES.TOTAL_FILES);
      return;
    }

    // Validate file sizes
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > MAX_FILE_SIZE) {
        setError(ERROR_MESSAGES.FILE_SIZE);
        return;
      }
    }

    // Validate file types
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    for (let i = 0; i < files.length; i++) {
      if (!allowedTypes.includes(files[i].type)) {
        setError(ERROR_MESSAGES.FILE_TYPE);
        return;
      }
    }

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('file', files[i]);
    }

    try {
      setUploading(true);
      setError(null);
      const uploadedReceipts = await apiService.uploadExpenseReceipts(report.id, formData);
      setReceipts([...receipts, ...uploadedReceipts]);
      setUploadDialogOpen(false);
      setSuccess('Receipts uploaded successfully');
    } catch (err) {
      setError('Failed to upload receipts: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteReceipt = async (receiptId) => {
    try {
      setError(null);
      await apiService.deleteExpenseReceipt(report.id, receiptId);
      setReceipts(receipts.filter(r => r.id !== receiptId));
      setSuccess('Receipt deleted successfully');
    } catch (err) {
      setError('Failed to delete receipt: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleMrlSubmit = async () => {
    try {
      // Save any pending MRL items first
      for (const item of mrlItems) {
        if (item.id.toString().startsWith('temp-')) {
          // This is a temporary item that needs to be created on the server
          await apiService.addMRLItem(report.id, item);
        }
      }
      
      // Refresh MRL items from server
      const response = await apiService.getOrCreateMonthlyReport({
        month: report.month,
        year: report.year
      });
      
      setMrlItems(response.mrlItems || []);
      setSuccess('Missing receipt ledger updated successfully');
    } catch (err) {
      setError('Failed to update missing receipt ledger: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = async () => {
    if (!items.length) {
      setError('Cannot submit an empty expense report');
      return;
    }

    if (!receipts.length && !mrlItems.length) {
      setError('Please attach at least one receipt or document missing receipts');
      return;
    }

    // If there are MRL items but no signature, prompt for signature
    if (mrlItems.length > 0 && !mrlSignature) {
      setError('Please sign the Missing Receipt Ledger before submitting');
      setActiveTab(2); // Switch to MRL tab
      return;
    }

    // Make sure any pending MRL items are saved
    if (mrlItems.some(item => item.id.toString().startsWith('temp-'))) {
      await handleMrlSubmit();
    }

    try {
      setSubmitting(true);
      setError(null);
      await apiService.submitExpenseReport(report.id);
      setSuccess('Report submitted successfully');
      setConfirmSubmitOpen(false);
      
      // Redirect to dashboard after successful submission
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      setError('Failed to submit report: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBankStatementImport = (importedItems) => {
    setItems(prevItems => [...prevItems, ...importedItems]);
    setSuccess('Bank statement expenses imported successfully');
    // Switch to expenses tab
    setActiveTab(0);
    // Reload the report to get the updated total
    loadReport();
  };

  // Function to display date without timezone shift
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    
    // Handle ISO date string with timezone (e.g., "2025-04-03T00:00:00.000Z")
    if (dateStr.includes('T')) {
      // Just take the date part before the 'T'
      dateStr = dateStr.split('T')[0];
    }
    
    // Split date string into parts (expected format: "YYYY-MM-DD")
    const parts = dateStr.split('-');
    
    // Format as MM/DD/YYYY with leading zeros
    if (parts.length === 3) {
      const year = parts[0];
      const month = parts[1].padStart(2, '0');
      const day = parts[2].padStart(2, '0');
      return `${month}/${day}/${year}`;
    }
    
    // Fallback to the original value if not in expected format
    return dateStr;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const canSubmit = report.status === 'draft';
  
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Expense Report - {format(new Date(report.year, report.month - 1), 'MMMM yyyy')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 1 }}>
          <Tab label="Expenses" />
          <Tab label="Bank Statement" />
          <Tab label="Missing Receipt Ledger" />
          <Tab label="Receipts" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Expenses</Typography>
              {canSubmit && (
                <Button
                  startIcon={<Plus size={20} />}
                  onClick={() => setNewItemDialogOpen(true)}
                  variant="contained"
                >
                  Add Expense
                </Button>
              )}
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Work Order</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    {canTagWebPortal && <TableCell align="center">Web Portal</TableCell>}
                    <TableCell>GL Account</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    {canSubmit && <TableCell align="center">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.work_order}</TableCell>
                      <TableCell>{formatDateDisplay(item.date)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{item.description}</span>
                          {item.is_web_portal && (
                            <Chip label="Web Portal" size="small" color="primary" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      {canTagWebPortal && (
                        <TableCell align="center">
                          <Checkbox
                            checked={Boolean(item.is_web_portal)}
                            disabled={!canSubmit}
                            onChange={async (e) => {
                              try {
                                const updated = await apiService.updateExpenseItem(report.id, item.id, {
                                  work_order: item.work_order,
                                  date: typeof item.date === 'string' && item.date.includes('T')
                                    ? item.date.split('T')[0]
                                    : item.date,
                                  description: item.description,
                                  gl_account: item.gl_account,
                                  amount: item.amount,
                                  is_web_portal: e.target.checked
                                });
                                setItems(items.map((row) => (row.id === updated.id ? updated : row)));
                                await loadReport();
                              } catch (err) {
                                setError('Failed to update Web Portal tag: ' + (err.response?.data?.message || err.message || err));
                              }
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell>{item.is_web_portal ? 'Web Portal' : item.gl_account}</TableCell>
                      <TableCell align="right">${(typeof item.amount === 'number' ? item.amount : parseFloat(item.amount) || 0).toFixed(2)}</TableCell>
                      {canSubmit && (
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={(event) => handleOpenActionMenu(event, item)}
                          >
                            <MoreVertical size={16} />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5 + (canTagWebPortal ? 1 : 0) + (canSubmit ? 1 : 0)} align="center">
                        No expenses added yet
                      </TableCell>
                    </TableRow>
                  )}
                  {items.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={4 + (canTagWebPortal ? 1 : 0)} align="right">
                        <Typography variant="subtitle1">Total:</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle1">
                          ${(typeof report?.total_amount === 'number' ? report.total_amount : parseFloat(report?.total_amount) || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      {canSubmit && <TableCell />}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {canTagWebPortal && <WebPortalAllocationPreview allocation={webPortalAllocation} />}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <BankStatementViewer 
            statement={bankStatement} 
            reportId={report.id}
            onImport={handleBankStatementImport}
            canImport={report.status === 'draft' && bankStatement && items.length === 0}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <MissingReceiptLedger
            reportId={report.id}
            mrlItems={mrlItems}
            setMrlItems={setMrlItems}
            mrlSignature={mrlSignature}
            setMrlSignature={setMrlSignature}
            readOnly={!canSubmit}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <ReceiptUploader
            reportId={report.id}
            receipts={receipts}
            setReceipts={setReceipts}
            readOnly={!canSubmit}
          />
        </TabPanel>

        {canSubmit && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button
              startIcon={<Send size={20} />}
              onClick={() => setConfirmSubmitOpen(true)}
              disabled={submitting || items.length === 0 || (receipts.length === 0 && mrlItems.length === 0)}
              variant="contained"
              color="primary"
            >
              {submitting ? 'Submitting...' : 'Submit Report'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Action Menu for Expense Items */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
      >
        <MenuItem 
          onClick={() => handleOpenEditDialog(items.find(item => item.id === actionMenuItemId))}
          sx={{ gap: 1 }}
        >
          <Edit size={16} />
          Edit
        </MenuItem>
        <MenuItem 
          onClick={() => handleOpenDeleteDialog(items.find(item => item.id === actionMenuItemId))}
          sx={{ gap: 1, color: 'error.main' }}
        >
          <Trash2 size={16} />
          Delete
        </MenuItem>
      </Menu>

      {/* Add Expense Dialog */}
      <Dialog
        open={newItemDialogOpen}
        onClose={() => setNewItemDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Expense</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Work Order"
                value={newItem.work_order}
                onChange={(e) => setNewItem({ ...newItem, work_order: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                value={newItem.date}
                onChange={(e) => setNewItem({ ...newItem, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={newItem.description}
                onChange={(e) => {
                  const description = e.target.value;
                  setNewItem({
                    ...newItem,
                    description,
                    is_web_portal: newItem.webPortalManual
                      ? newItem.is_web_portal
                      : descriptionLooksLikeWebPortal(description)
                  });
                }}
                required
              />
            </Grid>
            {canTagWebPortal && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(newItem.is_web_portal)}
                      onChange={(e) => setNewItem({
                        ...newItem,
                        is_web_portal: e.target.checked,
                        webPortalManual: true
                      })}
                    />
                  }
                  label="Web Portal — include this charge in the company-wide allocation"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <GLCodeField
                idPrefix="new-expense"
                label="GL Account"
                value={newItem.gl_account}
                onChange={(glAccount) => setNewItem((current) => ({ ...current, gl_account: glAccount }))}
                disabled={canTagWebPortal && newItem.is_web_portal}
                helperText={canTagWebPortal && newItem.is_web_portal
                  ? 'Coded by Web Portal allocation'
                  : 'Choose branch, department, and category, or type a code.'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Amount"
                value={newItem.amount}
                onChange={(e) => setNewItem({ ...newItem, amount: e.target.value })}
                required
                inputProps={{ step: "0.01" }}
                helperText="Use negative amounts for returns/refunds"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewItemDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddItem}
            variant="contained"
            disabled={!newItem.description || !newItem.amount || !newItem.date}
          >
            Add Expense
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Expense Dialog */}
      <Dialog
        open={editItemDialogOpen}
        onClose={() => setEditItemDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Expense</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Work Order"
                value={editItem.work_order}
                onChange={(e) => setEditItem({ ...editItem, work_order: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                value={editItem.date}
                onChange={(e) => setEditItem({ ...editItem, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={editItem.description}
                onChange={(e) => {
                  const description = e.target.value;
                  setEditItem({
                    ...editItem,
                    description,
                    is_web_portal: editItem.webPortalManual
                      ? editItem.is_web_portal
                      : descriptionLooksLikeWebPortal(description)
                  });
                }}
                required
              />
            </Grid>
            {canTagWebPortal && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(editItem.is_web_portal)}
                      onChange={(e) => setEditItem({
                        ...editItem,
                        is_web_portal: e.target.checked,
                        webPortalManual: true
                      })}
                    />
                  }
                  label="Web Portal — include this charge in the company-wide allocation"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <GLCodeField
                idPrefix="edit-expense"
                label="GL Account"
                value={editItem.gl_account}
                onChange={(glAccount) => setEditItem((current) => ({ ...current, gl_account: glAccount }))}
                disabled={canTagWebPortal && editItem.is_web_portal}
                helperText={canTagWebPortal && editItem.is_web_portal
                  ? 'Coded by Web Portal allocation'
                  : 'Choose branch, department, and category, or type a code.'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Amount"
                value={editItem.amount}
                onChange={(e) => setEditItem({ ...editItem, amount: e.target.value })}
                required
                inputProps={{ step: "0.01" }}
                helperText="Use negative amounts for returns/refunds"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItemDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditItem}
            variant="contained"
            disabled={!editItem.description || !editItem.amount || !editItem.date}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Expense Confirmation Dialog */}
      <Dialog
        open={confirmDeleteItemOpen}
        onClose={() => setConfirmDeleteItemOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this expense item? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteItemOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteItem}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Receipts Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploading && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Receipts</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Select receipts to upload (JPG, PNG, or PDF format, max 10MB each)
          </DialogContentText>
          <input
            accept="image/jpeg,image/jpg,image/png,application/pdf"
            style={{ display: 'none' }}
            id="receipt-upload"
            type="file"
            multiple
            onChange={handleFileUpload}
            disabled={uploading}
          />
          <label htmlFor="receipt-upload">
            <Button
              variant="outlined"
              component="span"
              fullWidth
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={20} /> : <Upload size={20} />}
              sx={{ mt: 2 }}
            >
              {uploading ? 'Uploading...' : 'Choose Files'}
            </Button>
          </label>
          <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
            Accepted formats: JPG, PNG, PDF (Maximum 50 files, 10MB each)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setUploadDialogOpen(false)} 
            disabled={uploading}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submit Confirmation Dialog */}
      <Dialog
        open={confirmSubmitOpen}
        onClose={() => !submitting && setConfirmSubmitOpen(false)}
      >
        <DialogTitle>Submit Expense Report</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to submit this expense report? 
            This action cannot be undone, and the report will be sent for review.
            Please ensure all receipts have been uploaded and missing receipts are documented.
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Summary:
            </Typography>
            <Typography variant="body2">
              • Number of Expenses: {items.length}
            </Typography>
            <Typography variant="body2">
              • Total Amount: ${(typeof report?.total_amount === 'number' ? report.total_amount : parseFloat(report?.total_amount) || 0).toFixed(2)}
            </Typography>
            <Typography variant="body2">
              • Number of Receipts: {receipts.length}
            </Typography>
            {mrlItems.length > 0 && (
              <Typography variant="body2">
                • Missing Receipt Items: {mrlItems.length}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmSubmitOpen(false)} 
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <Send size={20} />}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseReportForm;